
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/hooks/use-toast";

export interface ScriptureReference {
  reference: string;
  text: string;
  explanation?: string;
}

export interface MoralDilemmasResponse {
  directAnswer: string;
  biblicalFoundation: string;
  scriptureReferences: ScriptureReference[];
  culturalContrast: string;
  personalEncouragement: string;
  callToFaithfulness: string;
  practicalSteps: string[];
}

export const useMoralDilemmas = () => {
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<MoralDilemmasResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!question.trim()) {
      toast({
        title: "Please enter a question",
        description: "You need to enter a question to get biblical guidance.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Submitting moral dilemma question:", question);
      
      const { data, error: fnError } = await supabase.functions.invoke('moral-dilemmas', {
        body: { question: question.trim() },
      });
      
      if (fnError) {
        console.error("Supabase function error:", fnError);
        throw new Error(fnError.message || "Failed to get response from the server");
      }
      
      console.log("API Response:", data);
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (!data.response) {
        throw new Error("The response format was unexpected. Please try again.");
      }
      
      // Ensure scriptureReferences is always an array of valid objects
      if (!data.response.scriptureReferences || !Array.isArray(data.response.scriptureReferences)) {
        console.log("Response missing scriptureReferences array, creating empty array");
        data.response.scriptureReferences = [];
      } else {
        console.log(`Scripture references received: ${data.response.scriptureReferences.length}`);
      }
      
      setResponse(data.response);
      
      // Scroll to the response section
      setTimeout(() => {
        const responseElement = document.getElementById('response-section');
        if (responseElement) {
          responseElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      
    } catch (err: any) {
      console.error("Error fetching moral dilemmas response:", err);
      
      let errorMessage = "We encountered an error processing your question. Please try again.";
      
      if (err.message?.includes("OpenAI API key is not configured") || 
          err.message?.includes("Invalid OpenAI API key")) {
        errorMessage = "Server configuration error: The AI service is not properly configured. Please contact the administrator.";
      } else if (err.message?.includes("rate limit") || err.message?.includes("quota exceeded")) {
        errorMessage = "The AI service is currently experiencing high demand. Please try again later.";
      }
      
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveResponse = async () => {
    if (!response || !user) {
      toast({
        title: "Cannot save response",
        description: user ? "No response to save" : "Please sign in to save responses",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSaving(true);
      console.log("Saving moral dilemmas response...");
      
      const jsonString = JSON.stringify({
        question,
        response,
        createdAt: new Date().toISOString()
      });
      
      const contentObj = JSON.parse(jsonString);
      
      console.log("Content prepared for storage:", contentObj);
      
      const { error: insertError } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'moral_dilemmas',
          title: `Moral Dilemma: ${question.substring(0, 50)}${question.length > 50 ? '...' : ''}`,
          content: contentObj,
          user_id: user.id
        });

      if (insertError) {
        console.error("Error inserting saved response:", insertError);
        throw insertError;
      }

      toast({
        title: "Response Saved",
        description: "Your response has been saved to your dashboard."
      });
    } catch (error) {
      console.error('Error saving response:', error);
      toast({
        title: "Error",
        description: "Failed to save response. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const shareViaEmail = () => {
    if (!response) return;
    
    const subject = encodeURIComponent(`Biblical Guidance: ${question}`);
    const body = encodeURIComponent(`
I wanted to share this biblical response with you:

Question: ${question}

Direct Answer:
${response.directAnswer}

Biblical Foundation:
${response.biblicalFoundation}

~ Shared from Selah App
    `);
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    
    toast({
      title: "Email Client Opened",
      description: "Your email client should be opening to share this content.",
    });
  };

  const shareViaSMS = () => {
    if (!response) return;
    
    const text = encodeURIComponent(`
Biblical Guidance: ${question}

Direct Answer:
${response.directAnswer}

~ Shared from Selah App
    `);
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = `sms:?&body=${text}`;
    } else {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: "Copied to Clipboard",
          description: "The content has been copied to your clipboard as SMS sharing is only available on mobile devices.",
        });
      });
    }
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(facebookShareUrl, '_blank', 'width=600,height=400');
    
    toast({
      title: "Sharing to Facebook",
      description: "A Facebook share dialog should have opened.",
    });
  };

  const handleExampleClick = (example: string) => {
    setQuestion(example);
    document.getElementById("question-textarea")?.focus();
  };

  const handleResetResponse = () => {
    setResponse(null);
    // Optionally scroll to the form
    const formElement = document.getElementById('moral-dilemmas-form');
    formElement?.scrollIntoView({ behavior: 'smooth' });
  };

  return {
    question,
    setQuestion,
    isLoading,
    response,
    error,
    isSaving,
    handleSubmit,
    saveResponse,
    shareViaEmail,
    shareViaSMS,
    shareToFacebook,
    handleExampleClick,
    handleResetResponse,
  };
};
